import { batch, shallowEqual, useDispatch, useSelector } from 'react-redux';

import Button, { TButtonType } from '@r1-frontend/ui-react/components/buttons/button';
import { ButtonGroup } from '@r1-frontend/ui-react/components/buttons/buttonGroup/ButtonGroup';
import PopupResult, { EResultPopupStatus } from '@r1-frontend/ui-react/components/popups/popupResult';

import { reset } from '~/src/features/lk/PassportUpdate/store/actions';
import { startGettingGosuslugiCode } from '~/src/features/lk/PassportUpdate/store/thunks/startGettingGosuslugiCode';
import { TState } from '~/src/store';
import { openChat } from '~/src/store/actions/chat';

const enum EButtonActions {
    CLOSE = 'Закрыть',
    TRY_AGAIN = 'Попробовать ещё раз',
    CHAT = 'Написать в чат',
}

interface IContent {
    title: string,
    text: JSX.Element,
    popupType: EResultPopupStatus,
    btns: {
        name: EButtonActions,
        bType: TButtonType,
    }[],
}

const content: Record<string, IContent> = {
    success: {
        title: 'Теперь ваши данные актуальны',
        text: <></>,
        popupType: EResultPopupStatus.SUCCESS,
        btns: [
            {
                name: EButtonActions.CLOSE,
                bType: 'action',
            },
        ],
    },
    error: {
        title: 'Не получилось обновить данные',
        text: <>Пожалуйста, попробуйте ещё раз. Если ошибка повторится&nbsp;&mdash; напишите в&nbsp;чат техподдержки.</>,
        popupType: EResultPopupStatus.ERROR,
        btns: [
            {
                name: EButtonActions.TRY_AGAIN,
                bType: 'action',
            },
            {
                name: EButtonActions.CHAT,
                bType: 'secondary',
            },
        ],
    },
    incorrect: {
        title: 'Данные не совпадают',
        text: <>Данные в&nbsp;договоре не&nbsp;совпадают с&nbsp;вашими данными на&nbsp;Госуслугах. Пожалуйста, напишите в&nbsp;чат техподдержки.</>,
        popupType: EResultPopupStatus.WARNING,
        btns: [
            {
                name: EButtonActions.CHAT,
                bType: 'action',
            },
        ],
    },
};

const getContent = (isUpdateSuccess: boolean, isDataInCorrect: boolean) => isUpdateSuccess
    ? content['success']
    : isDataInCorrect
        ? content['incorrect']
        : content['error'];

/**
 * Попап с сообщением о результате обновления паспортных данных
 */
export const PassportUpdateResultPopup = () => {
    const dispatch = useDispatch();

    const { status, isUpdateSuccess, isDataInCorrect } = useSelector((state: TState) => {
        return ({
            status: state.passportUpdate.status,
            isUpdateSuccess: state.passportUpdate.isUpdateSuccess,
            isDataInCorrect: state.passportUpdate.isDataInCorrect,
        });
    }, shallowEqual);

    const onClick = (btnAction: EButtonActions) => {
        switch (btnAction) {
            case EButtonActions.CLOSE:
                return dispatch(reset());
            case EButtonActions.TRY_AGAIN:
                return dispatch(startGettingGosuslugiCode());
            case EButtonActions.CHAT:
                return batch(() => {
                    dispatch(reset());
                    dispatch(openChat());
                });
        }
    };

    const currentContent = getContent(isUpdateSuccess, isDataInCorrect);

    return (
        <>
            <PopupResult
                isOpen={status === 'result'}
                onClose={() => onClick(EButtonActions.CLOSE)}
                title={currentContent.title}
                text={currentContent.text}
                type={currentContent.popupType}
            >
                <ButtonGroup>
                    {currentContent.btns.map(btn => (
                        <Button
                            key={btn.name}
                            bType={btn.bType}
                            onClick={() => onClick(btn.name)}
                        >
                            {btn.name}
                        </Button>
                    ))}
                </ButtonGroup>
            </PopupResult>
        </>
    );
};
